import { useRouter } from '@tanstack/react-router';
import {
  RiDatabase2Line,
  RiNodeTree,
  RiLayoutGridLine,
  RiPlayLine,
  RiBarChartBoxLine,
  RiTeamLine,
  RiRocketLine,
  RiShieldLine,
  RiGitBranchLine,
  RiSparklingLine,
  RiTableLine,
  RiCursorLine,
  RiFilter3Line,
  RiCodeLine,
  RiBookmarkLine,
  RiTimeLine,
  RiLineChartLine,
  RiLayoutLine,
  RiShareLine,
  RiUserAddLine,
  RiLockLine,
  RiShareForwardLine,
  RiLightbulbLine,
  RiFileTextLine,
  RiSettings3Line,
  RiArrowRightLine,
  RiCloseLine,
} from '@remixicon/react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useOnboardingStore } from '@/stores/onboarding-store';

const iconMap: Record<string, React.ComponentType<{ className?: string; size?: number }>> = {
  Database: RiDatabase2Line,
  Network: RiNodeTree,
  Blocks: RiLayoutGridLine,
  Play: RiPlayLine,
  BarChart: RiBarChartBoxLine,
  Users: RiTeamLine,
  Rocket: RiRocketLine,
  Shield: RiShieldLine,
  Zap: RiSparklingLine,
  GitBranch: RiGitBranchLine,
  Sparkles: RiSparklingLine,
  Table: RiTableLine,
  MousePointer: RiCursorLine,
  Filter: RiFilter3Line,
  Code: RiCodeLine,
  BookmarkCheck: RiBookmarkLine,
  Clock: RiTimeLine,
  TrendingUp: RiLineChartLine,
  Layout: RiLayoutLine,
  Share: RiShareLine,
  UserPlus: RiUserAddLine,
  Lock: RiLockLine,
  Share2: RiShareForwardLine,
  Lightbulb: RiLightbulbLine,
  FileText: RiFileTextLine,
  Settings: RiSettings3Line,
};

const getIcon = (iconName: string) => {
  const IconComponent = iconMap[iconName];
  return IconComponent || RiDatabase2Line;
};

export function OnboardingDialog() {
  const router = useRouter();
  const {
    isOpen,
    currentStep,
    steps,
    closeOnboarding,
    nextStep,
    previousStep,
    goToStep,
    completeStep,
    completeOnboarding,
    skipOnboarding,
  } = useOnboardingStore();

  // if (currentStep < 0 || currentStep >= steps.length) return null;

  // const currentStepData = steps[currentStep];
  // const progress = ((currentStep + 1) / steps.length) * 100;

  // Safety check - don't render if no step data
  // if (!currentStepData) return null;

  if (
  currentStep < 0 ||
  currentStep >= steps.length ||
  !steps[currentStep]?.content
) {
  return null;
}

const currentStepData = steps[currentStep];
const progress = ((currentStep + 1) / steps.length) * 100;

  const handleActionClick = () => {
    const stepId = currentStepData.id;
    completeStep(stepId);

    // Navigate based on step
    switch (stepId) {
      case 'connect-database':
        closeOnboarding();
        router.navigate({ to: '/databases' });
        break;
      case 'schema-designer':
        closeOnboarding();
        router.navigate({ to: '/visual-schema-designer' });
        break;
      case 'visual-query-builder':
        closeOnboarding();
        router.navigate({ to: '/visual-query-builder' });
        break;
      case 'run-queries':
        closeOnboarding();
        router.navigate({ to: '/query' });
        break;
      case 'visualize-data':
        closeOnboarding();
        router.navigate({ to: '/analytics' });
        break;
      case 'collaborate':
        closeOnboarding();
        router.navigate({ to: '/team' });
        break;
      case 'explore-features':
        completeOnboarding();
        router.navigate({ to: '/dashboard' });
        break;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      nextStep();
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    skipOnboarding();
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeOnboarding}>
      <DialogContent className="max-w-[900px] w-[95vw] h-[90vh] max-h-[720px] p-0 gap-0 rounded-2xl bg-gray-50 dark:bg-gray-900 border-none shadow-2xl overflow-hidden">
        {/* Decorative ring */}
        <div className="absolute inset-0 pointer-events-none rounded-2xl ring-1 ring-inset ring-black/5 dark:ring-white/10" />

        <div className="flex h-full min-w-0 min-h-0">
          {/* Sidebar */}
          <div className="flex w-[300px] flex-col px-10 py-10 border-r border-gray-200 dark:border-gray-800">
            {/* Header */}
            <div className="mb-9">
              <h2 className="mb-2 text-xl font-semibold text-foreground-strong dark:text-gray-100">
                Get started with Moniva
              </h2>
              <p className="text-sm text-muted-foreground dark:text-gray-400">
                Learn the key features to work effectively with your databases.
              </p>
            </div>

            {/* Steps Navigation */}
            <div className="flex flex-col flex-1 gap-1 -mx-3">
              {steps.map((step, index) => {
                const StepIcon = getIcon(step.icon);
                const isActive = index === currentStep;
                const isCompleted = step.completed;

                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(index)}
                    className={cn(
                      'group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all',
                      isActive
                        ? 'bg-white text-foreground-strong shadow-sm ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-700'
                        : 'text-muted-foreground hover:bg-gray-50 hover:text-foreground dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100'
                    )}
                  >
                    {isActive && (
                      <div className="absolute left-0 w-1 h-6 -translate-y-1/2 bg-indigo-500 rounded-r-full top-1/2" />
                    )}

                    <div className="flex items-center justify-center flex-shrink-0 w-5 h-5">
                      <StepIcon
                        className={cn(
                          'h-5 w-5 transition-colors',
                          isActive
                            ? 'text-indigo-600 dark:text-indigo-400'
                            : isCompleted
                            ? 'text-green-600 dark:text-green-500'
                            : 'text-gray-400'
                        )}
                      />
                    </div>

                    <span className="flex-1 text-sm font-medium">
                      {step.title}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Progress */}
            <div className="mt-6">
              <div className="mb-4">
                <div className="flex items-center gap-1 text-xs font-medium tracking-wide uppercase text-muted-foreground dark:text-gray-400">
                  Step {currentStep + 1} of {steps.length}
                </div>
              </div>

              <div className="relative">
                <div className="h-2 border rounded-full border-gray-200/50 bg-gray-50 dark:border-gray-700/50 dark:bg-gray-800" />
                <div
                  className="absolute top-0 left-0 h-2 transition-all duration-700 ease-out rounded-full shadow-sm bg-indigo-500"
                  style={{ width: `${progress}%` }}
                >
                  <div
                    className="absolute inset-0 rounded-full"
                    style={{
                      opacity: 0.2,
                      backgroundImage:
                        'linear-gradient(45deg, rgba(255, 255, 255, 0.2) 25%, transparent 25%, transparent 50%, rgba(255, 255, 255, 0.2) 50%, rgba(255, 255, 255, 0.2) 75%, transparent 75%, transparent)',
                      backgroundSize: '8px 8px',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 p-2">
            <div className="relative h-full min-w-0 bg-white shadow-sm rounded-xl dark:bg-gray-950 flex flex-col">
              <div className="absolute inset-0 z-50 pointer-events-none rounded-xl ring-1 ring-inset ring-black/10 dark:ring-white/10" />

              {/* Scrollable Content Area */}
              <div className="flex-1 min-w-0 min-h-0 p-8 overflow-y-auto pr-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                {/* Header */}
                <div className="flex-shrink-0 min-w-0 mb-6">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-semibold text-foreground-strong dark:text-gray-100">
                      {currentStepData.content.title}
                    </h3>
                  </div>
                  <p className="max-w-md text-sm text-pretty text-muted-foreground dark:text-gray-400">
                    {currentStepData.content.description}
                  </p>
                </div>

                {/* Content */}
                <div className="space-y-6">
                  {/* Features */}
                  {currentStepData.content.features && (
                    <div className="space-y-4">
                      {currentStepData.content.features.map((feature, index) => {
                        const FeatureIcon = getIcon(feature.icon);
                        return (
                          <div
                            key={index}
                            className="flex gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-900 dark:border-gray-800"
                          >
                            <div className="flex-shrink-0">
                              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-500/10 dark:bg-indigo-500/20">
                                <FeatureIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="mb-1 text-sm font-semibold text-foreground-strong dark:text-gray-100">
                                {feature.title}
                              </h4>
                              <p className="text-sm text-muted-foreground dark:text-gray-400">
                                {feature.description}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Code Preview */}
                  {currentStepData.content.code && (
                    <div className="overflow-hidden border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-900 dark:border-gray-800">
                      <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
                        <div className="flex gap-1.5">
                          <div className="w-3 h-3 bg-red-500 rounded-full" />
                          <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                          <div className="w-3 h-3 bg-green-500 rounded-full" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground dark:text-gray-400">
                          Example Query
                        </span>
                      </div>
                      <div className="p-4 overflow-x-auto">
                        <pre className="font-mono text-sm text-foreground dark:text-gray-300">
                          <code>{currentStepData.content.code}</code>
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Fixed Actions Footer */}
              <div className="flex-shrink-0 px-8 pb-8 pt-6 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex gap-2">
                    {currentStep > 0 && (
                      <Button
                        variant="outline"
                        onClick={previousStep}
                        className="gap-2"
                      >
                        Previous
                      </Button>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      onClick={handleSkip}
                      className="text-muted-foreground dark:text-gray-400"
                    >
                      Skip tutorial
                    </Button>

                    {currentStep < steps.length - 1 ? (
                      <>
                        {currentStepData.content.action && (
                          <Button
                            onClick={handleActionClick}
                            className="gap-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
                          >
                            {currentStepData.content.action.label}
                            <RiArrowRightLine className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          onClick={handleNext}
                          variant="outline"
                          className="gap-2"
                        >
                          Next
                          <RiArrowRightLine className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => completeOnboarding()}
                        className="gap-2 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 flex-shrink-0"
                      >
                        Complete Tutorial
                        <RiRocketLine className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
